// WebAuthn-based biometric (Face ID / Touch ID / fingerprint) for local unlock.
// No server — uses the device's platform authenticator.
// The credentialId is stored locally; the biometric template never leaves the secure enclave.

function bytesToB64(buf) {
  const a = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < a.length; i++) s += String.fromCharCode(a[i]);
  return btoa(s);
}

function b64ToBytes(b64) {
  const s = atob(b64);
  const a = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) a[i] = s.charCodeAt(i);
  return a;
}

function randomBytes(len) {
  const a = new Uint8Array(len);
  crypto.getRandomValues(a);
  return a;
}

export function biometricSupported() {
  return Boolean(
    typeof window !== 'undefined' &&
    window.PublicKeyCredential &&
    typeof navigator.credentials?.create === 'function'
  );
}

export async function biometricPlatformAvailable() {
  if (!biometricSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch { return false; }
}

// Returns { credentialId } stored as base64 string. Throws on cancel/fail.
export async function registerBiometric() {
  const cred = await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32),
      rp: { name: 'ShiftReady' },
      user: {
        id: randomBytes(16),
        name: 'shift-session-' + Date.now(),
        displayName: 'Shift Session',
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },    // ES256
        { type: 'public-key', alg: -257 },  // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        requireResidentKey: false,
      },
      timeout: 60000,
      attestation: 'none',
    },
  });
  if (!cred) throw new Error('No credential');
  return { credentialId: bytesToB64(cred.rawId) };
}

// Returns true if biometric verified, false on cancel/timeout, throws on bad config.
export async function verifyBiometric(credentialIdB64) {
  try {
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge: randomBytes(32),
        allowCredentials: [
          { type: 'public-key', id: b64ToBytes(credentialIdB64) },
        ],
        userVerification: 'required',
        timeout: 60000,
      },
    });
    return Boolean(assertion);
  } catch (e) {
    // User canceled, timeout, or no match
    return false;
  }
}
